import { useEffect, useState } from 'react';
import { useTranslation } from '@infra/i18n';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shared/ui';
import { connectCoreSocket, onCoreEvent } from '@services/core';
import { endSession } from '@services/storage';
import {
  decodeJwtPayload,
  getAccessToken,
  hasAuthToken,
  sessionRevokedNotifier,
  SESSION_END_COPY_KEYS,
  type SessionEndReason,
} from '@infra/auth';

export function SessionRevokedGuard() {
  const { t } = useTranslation();
  const [reason, setReason] = useState<SessionEndReason | null>(null);

  useEffect(() => {
    return sessionRevokedNotifier.subscribe((r) => {
      // First reason wins — don't let a later trigger swap the copy mid-modal.
      setReason((prev) => prev ?? r);
    });
  }, []);

  useEffect(() => {
    if (!hasAuthToken()) return;

    // Stay out of the login exchange. Logging in again while an old token is still in
    // storage (any login not preceded by a clean logout) would otherwise kick the tab
    // with its OWN login: the server revokes the previous sid and pushes to the whole
    // user's room while `storeAuthTokens` hasn't run yet, so `mySid` still reads as the
    // sid being revoked. A tab genuinely superseded is never sitting on this route.
    if (window.location.pathname.startsWith('/login-callback')) return;

    connectCoreSocket();

    return onCoreEvent<{ revokedSids?: string[]; reason?: string }>(
      'auth:session-revoked',
      (payload) => {
        const mySid = decodeJwtPayload(getAccessToken() ?? undefined)?.sid;
        if (mySid && payload?.revokedSids?.includes(mySid)) {
          // Older servers send no `reason`; that push only ever meant a login elsewhere.
          const pushed = payload.reason;
          sessionRevokedNotifier.notify(
            pushed === 'deactivated' || pushed === 'deleted'
              ? pushed
              : pushed === 'password_changed'
                ? 'passwordChanged'
                : 'superseded'
          );
        }
      }
    );
  }, []);

  // Routing (SSO vs app root) lives entirely in LogoutCallback, reached via endSession()
  // for every reason — this component only picks the copy.
  const copyKey = SESSION_END_COPY_KEYS[reason ?? 'superseded'];

  const handleOk = () => {
    void endSession(reason ?? 'superseded');
  };

  return (
    <AlertDialog open={reason !== null}>
      {/* z-[2000] to sit above the roleplay surface (z-100) and its overlays (z-1000). */}
      <AlertDialogContent className="z-[2000]" overlayClassName="z-[2000]">
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`auth:${copyKey}.title`)}</AlertDialogTitle>
          <AlertDialogDescription>{t(`auth:${copyKey}.message`)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleOk}>{t(`auth:${copyKey}.ok`)}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
